import React, { useEffect, useState, useCallback } from "react";
import { superAdmin } from "../../api";
import {
  PageHeader, Badge, Btn, EmptyState,
  LoadingSpinner, ConfirmModal, FormField,
  inputStyle, selectStyle,
} from "../../components/ui";
import Modal from "../../components/ui/Modal";
import CreateFacilityModal from "./CreateFacilityModal";
import toast from "react-hot-toast";

export default function FacilitiesPage() {
  const [facilities,    setFacilities]    = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [search,        setSearch]        = useState("");
  const [showCreate,    setShowCreate]    = useState(false);
  const [editModal,     setEditModal]     = useState(null); // facility obj
  const [confirm,       setConfirm]       = useState(null); // { type, facility }
  const [actionLoading, setActionLoading] = useState(false);
  const [upgradeModal,  setUpgradeModal]  = useState(null); // facility obj

  const load = useCallback(() => {
    setLoading(true);
    superAdmin.listFacilities()
      .then(r => setFacilities(r.data?.results || r.data))
      .catch(() => toast.error("Failed to load facilities."))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = facilities.filter(f =>
    f.name.toLowerCase().includes(search.toLowerCase()) ||
    f.slug.toLowerCase().includes(search.toLowerCase())
  );

  const handleAction = async () => {
    if (!confirm) return;
    setActionLoading(true);
    try {
      if (confirm.type === "suspend") {
        await superAdmin.suspendFacility(confirm.facility.id);
        toast.success(`${confirm.facility.name} suspended.`);
      } else if (confirm.type === "activate") {
        await superAdmin.activateFacility(confirm.facility.id);
        toast.success(`${confirm.facility.name} activated.`);
      }
      load();
    } catch { toast.error("Action failed."); }
    finally  { setActionLoading(false); setConfirm(null); }
  };

  const handleResendInvite = async (facility) => {
    try {
      const { data } = await superAdmin.resendInvite(facility.id);
      toast.success(`Invite resent to ${facility.admin_email || "admin"}.`);
      if (data?.temp_password) {
        toast(`New temp password: ${data.temp_password}`, { duration: 12000, icon: "🔑" });
      }
    } catch { toast.error("Failed to resend invite."); }
  };

  return (
    <div style={styles.root}>
      <PageHeader
        title="Facilities"
        subtitle={`${facilities.length} facilit${facilities.length === 1 ? "y" : "ies"} on platform`}
        action={<Btn size="lg" onClick={() => setShowCreate(true)}>+ New Facility</Btn>}
      />

      <div style={styles.toolbar}>
        <input
          style={styles.search}
          placeholder="Search facilities…"
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
      </div>

      {loading ? <LoadingSpinner /> : filtered.length === 0 ? (
        <EmptyState
          icon="🏥"
          title={search ? "No facilities match your search" : "No facilities yet"}
          subtitle={search ? "Try a different search term." : "Create your first facility to get started."}
          action={!search && <Btn onClick={() => setShowCreate(true)}>+ Create First Facility</Btn>}
        />
      ) : (
        <div style={styles.tableWrap}>
          <table style={styles.table}>
            <thead>
              <tr>
                {["Facility", "Admin", "Tier", "Status", "Users", "Tickets Today", "Actions"].map(h => (
                  <th key={h} style={styles.th}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map(f => (
                <tr key={f.id} style={styles.tr}>
                  <td style={styles.td}>
                    <div style={styles.facilityName}>{f.name}</div>
                    <div style={styles.facilitySlug}>/{f.slug}</div>
                  </td>
                  <td style={styles.td}>
                    {f.admin_name ? (
                      <>
                        <div style={{ fontSize: "13px", color: "#E2E8F0" }}>{f.admin_name}</div>
                        <div style={{ fontSize: "11px", color: "#475569" }}>{f.admin_email}</div>
                      </>
                    ) : (
                      <span style={{ fontSize: "12px", color: "#EF4444" }}>No admin</span>
                    )}
                  </td>
                  <td style={styles.td}><Badge value={f.tier} /></td>
                  <td style={styles.td}><Badge value={f.status} /></td>
                  <td style={{ ...styles.td, textAlign: "center", color: "#94A3B8" }}>{f.user_count}</td>
                  <td style={{ ...styles.td, textAlign: "center", color: "#94A3B8" }}>{f.ticket_count}</td>
                  <td style={styles.td}>
                    <div style={styles.actions}>
                      {/* FIX 4: Edit button */}
                      <Btn size="sm" variant="ghost" onClick={() => setEditModal(f)}>Edit</Btn>

                      {/* Branding toggle */}
                      <Btn size="sm" variant="ghost"
                        onClick={async () => {
                          try {
                            await superAdmin.toggleFacilityBranding(f.id);
                            toast.success(`Branding ${f.show_vendor_branding ? "disabled" : "enabled"} for ${f.name}.`);
                            load();
                          } catch { toast.error("Failed to toggle branding."); }
                        }}
                        style={{ color: f.show_vendor_branding !== false ? "#22C55E" : "#64748B" }}
                      >
                        {f.show_vendor_branding !== false ? "🎨 Branding On" : "🎨 Branding Off"}
                      </Btn>

                      {f.status === "suspended" ? (
                        <Btn size="sm" variant="success"
                          onClick={() => setConfirm({ type: "activate", facility: f })}>
                          Activate
                        </Btn>
                      ) : (
                        <Btn size="sm" variant="warning"
                          onClick={() => setConfirm({ type: "suspend", facility: f })}>
                          Suspend
                        </Btn>
                      )}
                      {f.admin_email && (
                        <Btn size="sm" variant="ghost" onClick={() => handleResendInvite(f)}>
                          Resend Invite
                        </Btn>
                      )}
                      {!f.organisation_id && (
                        <Btn size="sm" variant="ghost"
                          onClick={() => setUpgradeModal(f)}
                          title="Upgrade this facility to an organisation with branches"
                          style={{ color: "#A78BFA", borderColor: "rgba(167,139,250,0.3)" }}
                        >
                          ↑ Upgrade to Org
                        </Btn>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <CreateFacilityModal
        open={showCreate}
        onClose={() => setShowCreate(false)}
        onCreated={() => { setShowCreate(false); load(); }}
      />

      <UpgradeToOrgModal
        open={!!upgradeModal}
        facility={upgradeModal}
        onClose={() => setUpgradeModal(null)}
        onUpgraded={() => { setUpgradeModal(null); load(); }}
      />

      {/* FIX 4: Edit Facility Modal */}
      <EditFacilityModal
        open={!!editModal}
        facility={editModal}
        onClose={() => setEditModal(null)}
        onSaved={() => { setEditModal(null); load(); }}
      />

      <ConfirmModal
        open={!!confirm}
        onClose={() => setConfirm(null)}
        onConfirm={handleAction}
        loading={actionLoading}
        title={confirm?.type === "suspend" ? "Suspend Facility?" : "Activate Facility?"}
        message={
          confirm?.type === "suspend"
            ? `${confirm?.facility?.name} will be suspended. Staff and patients will lose access.`
            : `${confirm?.facility?.name} will be reactivated.`
        }
        confirmLabel={confirm?.type === "suspend" ? "Suspend" : "Activate"}
        variant={confirm?.type === "suspend" ? "danger" : "success"}
      />
    </div>
  );
}

// ── Edit Facility Modal ───────────────────────────────────────────────────────
function EditFacilityModal({ open, facility, onClose, onSaved }) {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open && facility) {
      setForm({
        name:    facility.name    || "",
        email:   facility.email   || "",
        phone:   facility.phone   || "",
        address: facility.address || "",
        tier:    facility.tier    || "starter",
        status:  facility.status  || "trial",
      });
    }
  }, [open, facility]);

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error("Facility name is required."); return; }
    setSaving(true);
    try {
      await superAdmin.updateFacility(facility.id, form);
      toast.success(`${form.name} updated.`);
      onSaved();
    } catch (e) {
      const msg = e.response?.data?.detail
        || e.response?.data?.name?.[0]
        || "Failed to update facility.";
      toast.error(msg);
    } finally { setSaving(false); }
  };

  return (
    <Modal open={open} onClose={onClose} title="Edit Facility" width="520px">
      <div style={E.form}>
        {/* Name */}
        <FormField label="Facility Name" required>
          <input style={inputStyle} value={form.name || ""} onChange={e => set("name", e.target.value)} placeholder="Facility name" />
        </FormField>

        {/* Slug — read only, shown for reference */}
        <FormField label="Slug" hint="URL identifier — cannot be changed after creation">
          <input style={{ ...inputStyle, opacity: 0.5, cursor: "not-allowed" }} value={facility?.slug || ""} readOnly />
        </FormField>

        {/* Email + Phone */}
        <div style={E.row2}>
          <FormField label="Email">
            <input style={inputStyle} type="email" value={form.email || ""} onChange={e => set("email", e.target.value)} placeholder="admin@hospital.com" />
          </FormField>
          <FormField label="Phone">
            <input style={inputStyle} value={form.phone || ""} onChange={e => set("phone", e.target.value)} placeholder="030 XXX XXXX" />
          </FormField>
        </div>

        {/* Address */}
        <FormField label="Address">
          <textarea style={{ ...inputStyle, resize: "vertical", minHeight: "70px" }} value={form.address || ""} onChange={e => set("address", e.target.value)} placeholder="Full address" />
        </FormField>

        {/* Tier + Status */}
        <div style={E.row2}>
          <FormField label="Tier" hint="Determines feature limits">
            <select style={selectStyle} value={form.tier || "starter"} onChange={e => set("tier", e.target.value)}>
              <option value="starter">Starter</option>
              <option value="growth">Growth</option>
              <option value="enterprise">Enterprise</option>
            </select>
          </FormField>
          <FormField label="Status">
            <select style={selectStyle} value={form.status || "trial"} onChange={e => set("status", e.target.value)}>
              <option value="trial">Trial</option>
              <option value="active">Active</option>
              <option value="suspended">Suspended</option>
            </select>
          </FormField>
        </div>

        <div style={E.footer}>
          <Btn variant="ghost" onClick={onClose} disabled={saving}>Cancel</Btn>
          <Btn onClick={handleSave} disabled={saving || !form.name?.trim()}>
            {saving ? "Saving…" : "Save Changes"}
          </Btn>
        </div>
      </div>
    </Modal>
  );
}

function UpgradeToOrgModal({ open, facility, onClose, onUpgraded }) {
  const [form, setForm] = React.useState({ org_name: "", org_slug: "", tier: "starter" });
  const [saving, setSaving] = React.useState(false);
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
  React.useEffect(() => {
    if (open && facility) {
      setForm({ org_name: facility.name, org_slug: slug(facility.name), tier: facility.tier || "starter" });
    }
  }, [open, facility]);

  const handleUpgrade = async () => {
    setSaving(true);
    try {
      const res = await superAdmin.upgradeToOrg(facility.id, form);
      toast.success(res.data.message);
      onUpgraded();
    } catch (e) {
      toast.error(e.response?.data?.error || "Upgrade failed.");
    } finally { setSaving(false); }
  };

  return (
    <Modal open={open} onClose={onClose} title="Upgrade Facility to Organisation" width="500px">
      <div style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
        <div style={{ background: "rgba(167,139,250,0.06)", border: "1px solid rgba(167,139,250,0.2)", borderRadius: "10px", padding: "12px 16px", fontSize: "13px", color: "#94A3B8" }}>
          <strong style={{ color: "#A78BFA" }}>{facility?.name}</strong> will become the first branch of a new organisation.
          All existing zones, staff, settings, and queue history will be preserved.
        </div>
        <FormField label="Organisation Name" required>
          <input style={inputStyle} value={form.org_name} onChange={e => { set("org_name", e.target.value); if (!form.org_slug || form.org_slug === slug(form.org_name)) set("org_slug", slug(e.target.value)); }} />
        </FormField>
        <FormField label="Organisation Slug" required hint="Used in URLs — lowercase, no spaces">
          <input style={inputStyle} value={form.org_slug} onChange={e => set("org_slug", slug(e.target.value))} />
        </FormField>
        <FormField label="Tier">
          <select style={selectStyle} value={form.tier} onChange={e => set("tier", e.target.value)}>
            <option value="starter">Starter</option>
            <option value="growth">Growth</option>
            <option value="enterprise">Enterprise</option>
          </select>
        </FormField>
        <div style={{ display: "flex", gap: "10px", justifyContent: "flex-end" }}>
          <Btn variant="ghost" onClick={onClose}>Cancel</Btn>
          <Btn onClick={handleUpgrade} disabled={saving || !form.org_name.trim() || !form.org_slug.trim()}>
            {saving ? "Upgrading…" : "✓ Upgrade to Organisation"}
          </Btn>
        </div>
      </div>
    </Modal>
  );
}

const styles = {
  root: { padding: "32px", fontFamily: "'DM Sans','Segoe UI',sans-serif", maxWidth: "1200px" },
  toolbar: { marginBottom: "20px" },
  search: { background: "rgba(30,41,59,0.8)", border: "1px solid rgba(51,65,85,0.8)", borderRadius: "8px", padding: "9px 14px", color: "#F1F5F9", fontSize: "14px", outline: "none", width: "280px", fontFamily: "inherit" },
  tableWrap: { background: "rgba(15,23,42,0.6)", border: "1px solid rgba(37,99,235,0.15)", borderRadius: "14px", overflow: "hidden" },
  table: { width: "100%", borderCollapse: "collapse" },
  th: { padding: "12px 16px", fontSize: "11px", fontWeight: "700", color: "#475569", letterSpacing: "0.07em", textTransform: "uppercase", textAlign: "left", borderBottom: "1px solid rgba(37,99,235,0.1)", background: "rgba(15,23,42,0.8)" },
  tr: { borderBottom: "1px solid rgba(37,99,235,0.06)", transition: "background 0.1s" },
  td: { padding: "14px 16px", verticalAlign: "middle" },
  facilityName: { fontSize: "14px", fontWeight: "600", color: "#E2E8F0", marginBottom: "2px" },
  facilitySlug: { fontSize: "11px", color: "#334155", fontFamily: "monospace" },
  actions: { display: "flex", gap: "6px", flexWrap: "wrap" },
};

const E = {
  form:  { display: "flex", flexDirection: "column", gap: "16px" },
  row2:  { display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" },
  footer:{ display: "flex", justifyContent: "flex-end", gap: "10px", paddingTop: "8px", borderTop: "1px solid rgba(37,99,235,0.1)", marginTop: "4px" },
};
